/**
 * Pagina de Materiales recomendados (afiliados / store_materials).
 * La cabecera de la sección la pone el layout de /store/*.
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Plus, X, RefreshCw, Search, ShoppingBag } from 'lucide-react';
import type { StatusFilter, CategoryFilter, MaterialItem } from './types';
import { formatNumber } from '@/lib/format-es';
import { storeStatusLabel } from '@/lib/store-status';
import {
    useStoreStats,
    useStoreMaterials,
    useUpdateMaterialStatus,
    useDeleteMaterial,
    useUpdateMaterial,
} from './hooks/useStoreMaterials';
import {
    StoreStatsPanel,
    StoreMaterialCard,
    StoreCreateForm,
    StoreRejectDialog,
    StoreDeleteDialog,
} from './components';

export function MaterialsPage() {
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
    const [search, setSearch] = useState('');
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);

    const [rejectTarget, setRejectTarget] = useState<MaterialItem | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<MaterialItem | null>(null);

    const { data: stats } = useStoreStats();
    const { data: materials, isLoading: materialsLoading, refetch } = useStoreMaterials(statusFilter, categoryFilter, search);
    const statusMutation = useUpdateMaterialStatus();
    const deleteMutation = useDeleteMaterial();
    const updateMutation = useUpdateMaterial();

    const filteredCount = materials?.length || 0;

    const STATUS_TABS: { value: StatusFilter; label: string; count?: number }[] = [
        { value: 'all', label: 'Todos', count: stats?.total_materials },
        { value: 'approved', label: 'Aprobados', count: stats?.approved_count },
        { value: 'pending', label: 'Pendientes', count: stats?.pending_count },
        { value: 'rejected', label: 'Rechazados', count: stats?.rejected_count },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-end gap-2">
                <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                        showCreateForm
                            ? 'bg-muted text-muted-foreground hover:bg-muted/80'
                            : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
                    )}
                >
                    {showCreateForm ? <X size={14} /> : <Plus size={14} />}
                    {showCreateForm ? 'Cancelar' : 'Nuevo Material'}
                </button>
                <button
                    onClick={() => refetch()}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
                >
                    <RefreshCw size={14} />
                    Actualizar
                </button>
            </div>

            {showCreateForm && (
                <StoreCreateForm
                    onCreated={() => {
                        setShowCreateForm(false);
                        refetch();
                    }}
                    onCancel={() => setShowCreateForm(false)}
                />
            )}

            <StoreStatsPanel
                stats={stats}
                categoryFilter={categoryFilter}
                onCategoryChange={setCategoryFilter}
            />

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
                    {STATUS_TABS.map(t => (
                        <button
                            key={t.value}
                            onClick={() => setStatusFilter(t.value)}
                            className={cn(
                                'px-3 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap',
                                statusFilter === t.value
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {t.label}
                            {t.count !== undefined && t.count > 0 && (
                                <span className="ml-1.5 text-[10px] opacity-70">({t.count})</span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="relative flex-1 min-w-0 max-w-sm">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Buscar material..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
                    />
                </div>

                <span className="text-xs text-muted-foreground shrink-0">
                    {formatNumber(filteredCount)} resultado{filteredCount !== 1 ? 's' : ''}
                </span>
            </div>

            {materialsLoading ? (
                <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="h-24 rounded-xl border border-border bg-card animate-pulse" />
                    ))}
                </div>
            ) : !materials || materials.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center">
                    <ShoppingBag size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">
                        No hay materiales {statusFilter !== 'all' ? `con estado "${storeStatusLabel(statusFilter)}"` : ''}
                    </p>
                </div>
            ) : (
                <div className="space-y-2">
                    {materials.map(material => (
                        <StoreMaterialCard
                            key={material.id}
                            material={material}
                            isExpanded={expandedId === material.id}
                            isEditing={editingId === material.id}
                            onToggle={() => setExpandedId(expandedId === material.id ? null : material.id)}
                            onEdit={() => setEditingId(editingId === material.id ? null : material.id)}
                            onApprove={() => statusMutation.mutate({ materialId: material.id, newStatus: 'approved' })}
                            onReject={() => setRejectTarget(material)}
                            onDelete={() => setDeleteTarget(material)}
                            onSaveEdit={(data) => {
                                updateMutation.mutate({ p_material_id: material.id, ...data }, {
                                    onSuccess: () => setEditingId(null),
                                });
                            }}
                            isActioning={statusMutation.isPending || deleteMutation.isPending}
                        />
                    ))}
                </div>
            )}

            <StoreRejectDialog
                isOpen={!!rejectTarget}
                materialTitle={rejectTarget?.title ?? ''}
                onConfirm={(reason) => {
                    if (rejectTarget) {
                        statusMutation.mutate({
                            materialId: rejectTarget.id,
                            newStatus: 'rejected',
                            rejectionReason: reason,
                        });
                    }
                    setRejectTarget(null);
                }}
                onCancel={() => setRejectTarget(null)}
            />

            <StoreDeleteDialog
                isOpen={!!deleteTarget}
                material={deleteTarget}
                isDeleting={deleteMutation.isPending}
                onConfirm={() => {
                    if (deleteTarget) {
                        deleteMutation.mutate(deleteTarget.id, {
                            onSuccess: () => setDeleteTarget(null),
                        });
                    }
                }}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
}
